import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  LinearProgress,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSafeBack } from '../hooks/useSafeBack';
import assessmentService, { Question } from '../services/assessmentService';
import { toast } from 'react-toastify';

const TakeAssessmentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const goBack = useSafeBack('/assessments');
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Use ref to track if component is mounted
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    loadAssessmentData();
  }, [id]);

  useEffect(() => {
    // Cleanup function to prevent memory leaks and DOM issues
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadAssessmentData = async () => {
    if (!id) return;
    
    try {
      if (isMountedRef.current) {
        setLoading(true);
      }
      // Get assessment details
      const assessmentData = await assessmentService.getAssessmentById(parseInt(id));
      if (isMountedRef.current) {
        setAssessment(assessmentData);
      }
      
      // Get questions for this assessment
      const questionsData = await assessmentService.getAssessmentQuestions(parseInt(id));
      if (isMountedRef.current) {
        setQuestions(questionsData);
      }
    } catch (error) {
      console.error('Error loading assessment:', error);
      if (isMountedRef.current) {
        toast.error('Failed to load assessment');
        goBack();
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleAnswerChange = (questionId: number, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const unansweredQuestions = questions.filter(q => !answers[q.id]);
    if (unansweredQuestions.length > 0) {
      toast.warning(`Please answer all questions. ${unansweredQuestions.length} questions remaining.`);
      return;
    }

    try {
      if (isMountedRef.current) {
        setSubmitting(true);
      }
      
      // Prepare submission data
      const responses = questions.map(question => {
        const selectedOption = question.options?.find(opt => opt.optionText === answers[question.id]);
        return {
          questionId: question.id,
          answer: answers[question.id],
          pointValue: selectedOption?.pointValue || 0
        };
      });

      const submission = {
        surveyId: parseInt(id!),
        responses,
        totalScore: responses.reduce((sum, r) => sum + (r.pointValue || 0), 0),
        completedAt: new Date().toISOString()
      };

      // Submit the assessment - convert to expected format
      const submissionResponses = responses.map(r => ({
        questionId: r.questionId,
        selectedOptionIds: [], // Will be populated if needed
        responseText: r.answer,
        pointValue: r.pointValue
      }));
      
      // Calculate time spent (for now, use 10 minutes as default)
      const timeSpentMinutes = 10;
      
      await assessmentService.submitStudentAssessment(parseInt(id!), submissionResponses, timeSpentMinutes);
      
      if (isMountedRef.current) {
        toast.success('Assessment submitted successfully!');
        navigate('/results');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      if (isMountedRef.current) {
        toast.error('Failed to submit assessment');
      }
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!assessment || questions.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Assessment not found or no questions available.</Alert>
        <Button onClick={goBack} sx={{ mt: 2 }}>
          Back to Assessments
        </Button>
      </Container>
    );
  }

  const currentQ = questions[currentQuestion];

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button 
          variant="outlined" 
          onClick={goBack}
          size="small"
        >
          ← Back to Assessments
        </Button>
        <Typography variant="h4" component="h1">
          {assessment.title}
        </Typography>
      </Box>
      <Card>
        <CardContent>
          
          <Box sx={{ mb: 3 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Question {currentQuestion + 1} of {questions.length}
            </Typography>
          </Box>

          <Box sx={{ minHeight: 300 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ fontSize: '1.2rem', mb: 2 }}>
                {currentQ.questionText}
              </FormLabel>
              <RadioGroup
                value={answers[currentQ.id] || ''}
                onChange={(e) => handleAnswerChange(currentQ.id, e.target.value)}
              >
                {currentQ.options?.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option.optionText}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography>{option.optionText}</Typography>
                        {typeof option.pointValue === 'number' && (
                          <Typography variant="caption" color="text.secondary">
                            Points: {option.pointValue}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ mb: 1 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
            >
              Previous
            </Button>
            
            {currentQuestion === questions.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : 'Submit Assessment'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!answers[currentQ.id]}
              >
                Next
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TakeAssessmentPage;